import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { cn } from '../lib/cn';
import type { StatusTone } from '../lib/status-tone';

export type AlertTone = StatusTone;

const toneClass: Record<AlertTone, string> = {
  info: 'border-ds-brand/30 bg-ds-brand-subtle text-ds-text',
  success: 'border-ds-success/25 bg-ds-success-subtle text-ds-text',
  warning: 'border-ds-warning/30 bg-ds-warning-subtle text-ds-text',
  danger: 'border-ds-danger/25 bg-ds-danger-subtle text-ds-text',
};

const iconClass: Record<AlertTone, string> = {
  info: 'text-ds-brand',
  success: 'text-ds-success',
  warning: 'text-ds-warning',
  danger: 'text-ds-danger',
};

const icons: Record<AlertTone, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: AlertCircle,
};

/**
 * Live-region role by tone.
 * - `warning` / `danger` → assertive `alert` (operational interruptions — payment
 *   exclusion, schedule incomplete, etc.). Matches former product Alert.
 * - `info` / `success` → polite `status` (advisory, non-interruptive).
 */
function roleForTone(tone: AlertTone): 'alert' | 'status' {
  return tone === 'warning' || tone === 'danger' ? 'alert' : 'status';
}

export type AlertProps = {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
};

/** Inline banner — not a toast. Soft fill + semantic accent. */
export function Alert({
  tone = 'info',
  title,
  children,
  className,
  action,
}: AlertProps) {
  const Icon = icons[tone];
  return (
    <div
      role={roleForTone(tone)}
      className={cn(
        'flex gap-2.5 rounded-ds-lg border px-3 py-2.5 text-ds-sm',
        toneClass[tone],
        className,
      )}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', iconClass[tone])} aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <div className="font-medium text-ds-text">{title}</div> : null}
        <div className="text-ds-text-secondary">{children}</div>
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}
